import { AlertTriangle, Plug } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import {
  approveMcpAuthorizeRequest,
  getMcpAuthorizeRequest,
  isApiError,
  type McpAuthorizeRequest,
} from "@/lib/api";
import { logger } from "@/lib/logger";

/**
 * Consent screen for an MCP client connecting over OAuth.
 *
 * The backend's `/authorize` endpoint cannot tell who the browser belongs to,
 * so it parks the request and sends the user here. This page sits behind the
 * normal session guard, which is what ties the connection to a real signed-in
 * person, and approving it is the only path that mints an authorization code.
 */
export function McpAuthorizePage() {
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("request_id");

  const [request, setRequest] = useState<McpAuthorizeRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!requestId) {
      setError("This connection link is missing its request id.");
      setLoading(false);
      return;
    }
    getMcpAuthorizeRequest({ path: { request_id: requestId } })
      .then(({ data }) => setRequest(data ?? null))
      .catch((err) => {
        logger.error("[mcp-authorize] could not load request:", err);
        const detail = isApiError(err)
          ? (err.body as { detail?: string } | undefined)?.detail
          : undefined;
        setError(detail || "This connection request is no longer valid.");
      })
      .finally(() => setLoading(false));
  }, [requestId]);

  const handleApprove = async () => {
    if (!requestId) {
      return;
    }
    setApproving(true);
    setError("");
    try {
      const { data } = await approveMcpAuthorizeRequest({ body: { request_id: requestId } });
      if (!data?.redirect_url) {
        throw new Error("No redirect target returned");
      }
      // Hand the browser back to the MCP client with the authorization code.
      window.location.href = data.redirect_url;
    } catch (err) {
      logger.error("[mcp-authorize] approval failed:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError(detail || "Could not approve this connection. Please try again.");
      setApproving(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col justify-center px-6 py-16">
      <div className="bg-card border-border rounded-lg border p-6">
        <div className="flex items-center gap-3">
          <Plug className="text-primary size-5" />
          <h1 className="text-primary text-xl font-normal">Connect to Scaffold</h1>
        </div>

        {loading ? (
          <p className="text-muted mt-4 text-sm">Checking this connection request...</p>
        ) : request ? (
          <>
            <p className="text-foreground mt-4 text-sm">
              <span className="font-medium">{request.client_name}</span> wants to use Scaffold on
              your behalf. It acts as you. It can use the Scaffold actions this connection exposes,
              with your account&rsquo;s permissions, including creating projects and clients and
              deleting a project&rsquo;s external resources.
            </p>
            {/* Anyone can register a client under any name, so the name proves
                nothing. The destination is the part worth checking. */}
            <div className="border-border bg-muted/30 mt-4 rounded-md border p-3">
              <p className="text-muted text-xs">This connection will be sent to</p>
              <p className="text-foreground mt-1 font-mono text-sm break-all">
                {request.redirect_host}
              </p>
            </div>
            <p className="text-muted mt-3 text-xs">
              Approve this only if you just started a connection yourself and you recognise that
              address. The name above is chosen by whoever built the tool and is not verified.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <Button onClick={handleApprove} disabled={approving}>
                {approving ? "Connecting..." : "Approve connection"}
              </Button>
              <Button variant="ghost" onClick={() => window.close()} disabled={approving}>
                Cancel
              </Button>
            </div>
          </>
        ) : null}

        {error && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
}
